import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Popconfirm,
  Tag,
  Drawer,
  List,
  Avatar,
  Select,
  Empty,
  Spin
} from 'antd';
import './styles.css';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  getUserGroups,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
  getUserGroup,
  addGroupMembers,
  removeGroupMember,
  GroupType,
  type UserGroup,
  type UserGroupWithMembers,
  type UserGroupMember
} from '../../services/userGroup';
import { getAssignableUsers, type UserAssignee } from '../../services/user';
import { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 分组类型选项
const GROUP_TYPE_OPTIONS = [
  { label: '办案', value: GroupType.CASE_HANDLING },
  { label: '后端组', value: GroupType.BACKEND },
  { label: '售后', value: GroupType.AFTER_SALES },
  { label: '文书写作', value: GroupType.DOCUMENT_WRITING },
  { label: '立案', value: GroupType.CASE_FILING },
  { label: '案管', value: GroupType.CASE_MANAGEMENT },
];

// 分组类型颜色映射
const GROUP_TYPE_COLORS: Record<string, string> = {
  '办案': 'blue',
  '后端组': 'green',
  '售后': 'orange',
  '文书写作': 'purple',
  '立案': 'cyan',
  '案管': 'magenta',
};

const UserGroupPage: React.FC = () => {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');

  // Modal states
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);

  // Drawer states
  const [memberDrawerVisible, setMemberDrawerVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<UserGroupWithMembers | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // Member management states
  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserAssignee[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserGroups({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        keyword: searchKeyword || undefined,
      });
      setGroups(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      message.error('获取用户分组失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchKeyword]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch group details with members
  const fetchGroupDetails = async (groupId: number) => {
    setMemberLoading(true);
    try {
      const group = await getUserGroup(groupId);
      setSelectedGroup(group);
    } catch (error) {
      console.error('Failed to fetch group details:', error);
      message.error('获取分组详情失败');
    } finally {
      setMemberLoading(false);
    }
  };

  // Fetch available users for adding to group
  const fetchAvailableUsers = async () => {
    try {
      const users = await getAssignableUsers({ limit: 200 });
      setAvailableUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      message.error('获取用户列表失败');
    }
  };

  // Handle create group
  const handleCreate = async (values: any) => {
    try {
      await createUserGroup(values);
      message.success('创建用户分组成功');
      setCreateModalVisible(false);
      form.resetFields();
      fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      message.error('创建用户分组失败');
    }
  };

  // Handle update group
  const handleUpdate = async (values: any) => {
    if (!editingGroup) return;

    try {
      await updateUserGroup(editingGroup.id, values);
      message.success('更新用户分组成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Failed to update group:', error);
      message.error('更新用户分组失败');
    }
  };

  // Handle delete group
  const handleDelete = async (groupId: number) => {
    try {
      await deleteUserGroup(groupId);
      message.success('删除用户分组成功');
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
      message.error('删除用户分组失败');
    }
  };

  // Handle add members
  const handleAddMembers = async () => {
    if (!selectedGroup) return;

    try {
      await addGroupMembers(selectedGroup.id, { user_ids: selectedUserIds });
      message.success('添加成员成功');
      setAddMemberModalVisible(false);
      setSelectedUserIds([]);
      fetchGroupDetails(selectedGroup.id);
    } catch (error) {
      console.error('Failed to add members:', error);
      message.error('添加成员失败');
    }
  };

  // Handle remove member
  const handleRemoveMember = async (userId: number) => {
    if (!selectedGroup) return;

    try {
      await removeGroupMember(selectedGroup.id, userId);
      message.success('移除成员成功');
      fetchGroupDetails(selectedGroup.id);
    } catch (error) {
      console.error('Failed to remove member:', error);
      message.error('移除成员失败');
    }
  };

  // Show members drawer
  const showMembersDrawer = (group: UserGroup) => {
    setMemberDrawerVisible(true);
    fetchGroupDetails(group.id);
  };

  // Show add member modal
  const showAddMemberModal = () => {
    fetchAvailableUsers();
    setAddMemberModalVisible(true);
  };

  // Show create modal
  const showCreateModal = () => {
    fetchAvailableUsers();
    setCreateModalVisible(true);
  };

  // Show edit modal
  const showEditModal = (record: UserGroup) => {
    fetchAvailableUsers();
    setEditingGroup(record);
    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      group_type: record.group_type,
      leader_id: record.leader_id,
    });
    setEditModalVisible(true);
  };

  // Table columns
  const columns: ColumnsType<UserGroup> = [
    {
      title: '分组名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '分组类型',
      dataIndex: 'group_type',
      key: 'group_type',
      width: 120,
      render: (type: string) => (
        <Tag color={GROUP_TYPE_COLORS[type] || 'default'}>{type}</Tag>
      ),
    },
    {
      title: '组长',
      dataIndex: 'leader_name',
      key: 'leader_name',
      width: 120,
      render: (name: string) => name || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '成员数量',
      dataIndex: 'member_count',
      key: 'member_count',
      width: 100,
      render: (count) => count || 0,
    },
    {
      title: '创建人',
      dataIndex: 'creator_name',
      key: 'creator_name',
      width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<TeamOutlined />}
            onClick={() => showMembersDrawer(record)}
          >
            成员
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该分组吗？"
            description="删除后数据将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Filter out existing members from available users
  const filteredUsers = (availableUsers || []).filter(
    user => !(selectedGroup?.members || []).some(member => member.user_id === user.id)
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4}>用户分组管理</Title>
            <Space>
              <Input.Search
                placeholder="搜索分组名称或描述"
                allowClear
                onSearch={(value) => {
                  setSearchKeyword(value);
                  setCurrentPage(1);
                }}
                style={{ width: 250 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showCreateModal}
              >
                新建分组
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={groups}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
          />
        </Space>
      </Card>

      {/* Create Modal */}
      <Modal
        title="新建用户分组"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="name"
            label="分组名称"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="请输入分组名称" />
          </Form.Item>

          <Form.Item
            name="group_type"
            label="分组类型"
            rules={[{ required: true, message: '请选择分组类型' }]}
          >
            <Select placeholder="请选择分组类型">
              {GROUP_TYPE_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leader_id"
            label="组长"
          >
            <Select
              placeholder="请选择组长（可选）"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(availableUsers || []).map(user => ({
                value: user.id,
                label: user.real_name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="member_ids"
            label="组员"
          >
            <Select
              mode="multiple"
              placeholder="请选择组员（可选）"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(availableUsers || []).map(user => ({
                value: user.id,
                label: user.real_name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea
              rows={3}
              placeholder="请输入分组描述"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="编辑用户分组"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditingGroup(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="name"
            label="分组名称"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="请输入分组名称" />
          </Form.Item>

          <Form.Item
            name="group_type"
            label="分组类型"
            rules={[{ required: true, message: '请选择分组类型' }]}
          >
            <Select placeholder="请选择分组类型">
              {GROUP_TYPE_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leader_id"
            label="组长"
          >
            <Select
              placeholder="请选择组长（可选）"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(availableUsers || []).map(user => ({
                value: user.id,
                label: user.real_name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea
              rows={3}
              placeholder="请输入分组描述"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setEditingGroup(null);
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Members Drawer */}
      <Drawer
        title={
          <Space>
            <TeamOutlined />
            <span>{selectedGroup?.name} - 成员管理</span>
          </Space>
        }
        placement="right"
        width={500}
        open={memberDrawerVisible}
        onClose={() => {
          setMemberDrawerVisible(false);
          setSelectedGroup(null);
        }}
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={showAddMemberModal}
          >
            添加成员
          </Button>
        }
      >
        {memberLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : !selectedGroup?.members || selectedGroup.members.length === 0 ? (
          <Empty
            description="暂无成员"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={selectedGroup?.members || []}
            renderItem={(member: UserGroupMember) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="确定移除该成员吗？"
                    onConfirm={() => handleRemoveMember(member.user_id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" danger size="small">
                      移除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={member.user_name || '未知用户'}
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary">{member.user_email}</Text>
                      {member.user_role && (
                        <Tag color="blue">{member.user_role}</Tag>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      {/* Add Members Modal */}
      <Modal
        title="添加成员"
        open={addMemberModalVisible}
        onCancel={() => {
          setAddMemberModalVisible(false);
          setSelectedUserIds([]);
        }}
        onOk={handleAddMembers}
        okText="添加"
        cancelText="取消"
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="请选择要添加的成员"
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={(filteredUsers || []).map(user => ({
            value: user.id,
            label: user.real_name,
          }))}
        />
      </Modal>
    </div>
  );
};

export default UserGroupPage;
